import { Component,Input,Output,EventEmitter, OnInit, inject, signal, computed } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { NgbDateStruct, NgbCalendar, NgbDate, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
    selector: 'app-fecha-turno',
    templateUrl: './fecha-turno.component.html',
    styleUrls: ['./fecha-turno.component.css'],
    standalone: false
})
export class FechaTurnoComponent implements OnInit {
  private _router = inject(Router);
  private _calendar = inject(NgbCalendar);
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _usuarioService = inject(UsuarioService);
  private _fb = inject(FormBuilder);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private _location = inject(Location);

  // ------- estado -------
  error = signal<string>('');

  // loading global (pantalla entera)
  loadingGlobal = computed(() => this._loadingService.isLoading());

  // loading local (botón)
  loadingLocal = signal<boolean>(false);

  // tooltip ayuda
  helpOpen = signal<boolean>(false);
  helpText = signal<string>('Seleccioná hasta 3 fechas y 3 opciones de horarios para avanzar.');

  // Form
  fechaTurno = this._fb.nonNullable.group({
    manana: [false],
    tarde: [false],
    noche: [false],
  });

  // flags guardados en orden
  b_manana = false;
  b_tarde = false;
  b_noche = false;
  
  // calendario
  _datesSelected: NgbDateStruct[] = [];
  
  @Input()
  set datesSelected(value: NgbDateStruct[]) {
    this._datesSelected = value;
  }
  get datesSelected(): NgbDateStruct[] {
    return this._datesSelected ? this._datesSelected : [];
  }

  @Output() datesSelectedChange = new EventEmitter<NgbDateStruct[]>();

  date: { year: number; month: number } = {
    year: this._calendar.getToday().year,
    month: this._calendar.getToday().month,
  };

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        // si quedó guest -> sign-in
        if (this._authService.session() === 'guest') {
          this._router.navigate(['/sign-in']);
          return;
        }

        // rehidratar estado desde orden
        const ord = this._orderPrestadorService.ordenPrestador;

        this.b_manana = !!ord.b_manana;
        this.b_tarde = !!ord.b_tarde;
        this.b_noche = !!ord.b_noche;

        // fechas guardadas (Date -> NgbDate)
        if (ord.d_fecha_uno) {
          this.datesSelected.push(new NgbDate(ord.d_fecha_uno.getFullYear(), ord.d_fecha_uno.getMonth() + 1, ord.d_fecha_uno.getDate()));
        }
        if (ord.d_fecha_dos) {
          this.datesSelected.push(new NgbDate(ord.d_fecha_dos.getFullYear(), ord.d_fecha_dos.getMonth() + 1, ord.d_fecha_dos.getDate()));
        }
        if (ord.d_fecha_tres) {
          this.datesSelected.push(new NgbDate(ord.d_fecha_tres.getFullYear(), ord.d_fecha_tres.getMonth() + 1, ord.d_fecha_tres.getDate()));
        }

        this.showForm();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
      }
    });
  }

  today(datepicker: NgbDatepicker) {
    const { calendar } = datepicker;
    datepicker.navigateTo(calendar.getToday());
  }

  isSelected = (date: NgbDate) => {
    return this.datesSelected.findIndex((f) => f.day == date.day && f.month == date.month && f.year == date.year) >= 0;
  };

  selectOne(date: NgbDate) {
    const index = this.datesSelected.findIndex((f) => f.day == date.day && f.month == date.month && f.year == date.year);

    if (index >= 0) {
      // If exist, remove the date
      this.datesSelected.splice(index, 1);
    } else {
      // a simple push
      if (this.datesSelected.length <= 2) {
        this.datesSelected.push(date);
      }
    }

    // si querés notificar al padre
    this.datesSelectedChange.emit(this.datesSelected);
  }

  showForm() {
    this.fechaTurno.patchValue({
      manana: this.b_manana,
      tarde: this.b_tarde,
      noche: this.b_noche,
    });
  }

  async saveFechaTurno(): Promise<void> {
    this.error.set('');
    this.loadingLocal.set(true);

    try {
      const v = this.fechaTurno.getRawValue();

      if (!v.manana && !v.tarde && !v.noche) {
        this.error.set('Debe seleccionar al menos un horario');
        return;
      }

      if (this.datesSelected.length === 0) {
        this.error.set('Debe seleccionar al menos una fecha');
        return;
      }

      // Obtener la fecha actual sin la hora
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Función auxiliar para convertir las fechas seleccionadas a objetos Date
      const toDate = (day: number, month: number, year: number): Date | null => {
        return day && month && year ? new Date(year, month - 1, day) : null;
      };

      // Convertir las fechas seleccionadas
      const fecha_uno = this.datesSelected.at(0) ? toDate(this.datesSelected.at(0)!.day, this.datesSelected.at(0)!.month, this.datesSelected.at(0)!.year) : null;
      const fecha_dos = this.datesSelected.at(1) ? toDate(this.datesSelected.at(1)!.day, this.datesSelected.at(1)!.month, this.datesSelected.at(1)!.year) : null;
      const fecha_tres = this.datesSelected.at(2) ? toDate(this.datesSelected.at(2)!.day, this.datesSelected.at(2)!.month, this.datesSelected.at(2)!.year) : null;

      // Verificar si alguna fecha es anterior a hoy
      if ((fecha_uno && fecha_uno < today) || (fecha_dos && fecha_dos < today) || (fecha_tres && fecha_tres < today)) {
        this.error.set('Las fechas no pueden ser anteriores al día de hoy');
        return;
      }

      // Guardar en la orden (GLOBAL loading)
      await this._loadingService.run(async () => {
        const ord = this._orderPrestadorService.ordenPrestador;

        // Conversión a Date: Date usa meses basados en índice (0 = enero, 11 = diciembre). Por eso se resta 1 al valor de month.
        ord.d_fecha_uno = this.datesSelected.at(0) ? new Date(this.datesSelected.at(0)!.year, this.datesSelected.at(0)!.month - 1, this.datesSelected.at(0)!.day) : null;
        ord.d_fecha_dos = this.datesSelected.at(1) ? new Date(this.datesSelected.at(1)!.year, this.datesSelected.at(1)!.month - 1, this.datesSelected.at(1)!.day) : null;
        ord.d_fecha_tres = this.datesSelected.at(2) ? new Date(this.datesSelected.at(2)!.year, this.datesSelected.at(2)!.month - 1, this.datesSelected.at(2)!.day) : null;

        ord.b_manana = v.manana;
        ord.b_tarde = v.tarde;
        ord.b_noche = v.noche;

        // Turno pedido directamente a un prestador puntual: resolver en qué lugar es.
        // Si el backend decide a qué prestadores avisar (flujo por especialidad), esto no aplica.
        if (ord.id_prestador) {
          try {
            const ubicaciones = await firstValueFrom(
              this._usuarioService.getUbicaciones(ord.id_prestador, true)
            );
            this._orderPrestadorService.ubicacionesPrestador = ubicaciones ?? [];

            if ((ubicaciones?.length ?? 0) > 1) {
              this._router.navigate(['/seleccion-ubicacion-prestador']);
              return;
            }

            const unica = ubicaciones?.[0];
            ord.id_ubicacion = unica?.id ?? null;
            this._orderPrestadorService.ubicacionSeleccionada = unica;
          } catch (eUbi) {
            // No bloqueamos el flujo por esto: en el peor caso el backend resuelve la ubicación igual.
            console.error('No se pudieron obtener las ubicaciones del prestador', eUbi);
          }
        }

        this._router.navigate(['/resumen-order-prestador']);
      });
      } finally {
        this.loadingLocal.set(false);
      }
  }

  openhelp(): void {
    this.helpOpen.set(!this.helpOpen());
  }

  back() {
    this._location.back();
  }
}


